// Month-vs-month comparison helpers for the retail dashboard.
// Pure functions only (no React, antd or translations) so they can be unit
// tested in isolation. Calendar math is UTC, matching the dashboard API.

// Changes smaller than this (in percentage points) are shown as neutral.
export const NEUTRAL_BAND_PP = 0.5;
// Fewer stock checks than this on either side makes a rate delta unreliable.
export const LOW_SAMPLE_CHECKS = 30;
// Comparison months may reach this many months before the current month.
export const COMPARE_LOOKBACK_MONTHS = 12;

const pad = (value) => String(value).padStart(2, "0");

const isFiniteNumber = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value));

export const parseMonthKey = (key) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(key || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  return month >= 0 && month <= 11 ? { year, month } : null;
};

export const toMonthKey = ({ year, month }) => {
  const normalized = new Date(Date.UTC(year, month, 1));
  return `${normalized.getUTCFullYear()}-${pad(normalized.getUTCMonth() + 1)}`;
};

export const shiftMonthKey = (key, delta) => {
  const parsed = parseMonthKey(key);
  return parsed
    ? toMonthKey({ year: parsed.year, month: parsed.month + delta })
    : null;
};

export const daysInMonth = ({ year, month }) =>
  new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

// `todayIso` is the UTC calendar date "YYYY-MM-DD".
const parseIsoDay = (isoDay) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDay || ""));
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3]),
  };
};

export const currentMonthKey = (todayIso) => {
  const today = parseIsoDay(todayIso);
  return today ? toMonthKey(today) : null;
};

export const earliestComparableMonthKey = (todayIso) =>
  shiftMonthKey(currentMonthKey(todayIso), -COMPARE_LOOKBACK_MONTHS);

/**
 * Whether `key` is a month the comparison may use: not in the future and not
 * older than COMPARE_LOOKBACK_MONTHS. Month keys compare lexicographically.
 */
export const isComparableMonth = (key, todayIso) => {
  const current = currentMonthKey(todayIso);
  const earliest = earliestComparableMonthKey(todayIso);
  return Boolean(
    parseMonthKey(key) && current && key <= current && key >= earliest,
  );
};

/**
 * Resolve the two date ranges to compare. When either month is the current
 * (partial) month, both are limited to the same days of the month: day 1 up
 * to today's day, clamped to each month's length ("same days"). Otherwise
 * both months are compared in full.
 *
 * Returns null when the months are invalid, identical or out of range.
 */
export const resolveComparisonRanges = ({ monthA, monthB, todayIso }) => {
  const today = parseIsoDay(todayIso);
  const a = parseMonthKey(monthA);
  const b = parseMonthKey(monthB);
  if (!today || !a || !b || monthA === monthB) return null;
  if (!isComparableMonth(monthA, todayIso)) return null;
  if (!isComparableMonth(monthB, todayIso)) return null;

  const current = toMonthKey(today);
  const sameDays = monthA === current || monthB === current;
  const rangeFor = (month) => {
    const lastDay = sameDays
      ? Math.min(today.day, daysInMonth(month))
      : daysInMonth(month);
    const prefix = `${month.year}-${pad(month.month + 1)}`;
    return {
      monthKey: toMonthKey(month),
      fromDate: `${prefix}-01`,
      toDate: `${prefix}-${pad(lastDay)}`,
      lastDay,
      isCurrentMonth: toMonthKey(month) === current,
    };
  };

  return { sameDays, a: rangeFor(a), b: rangeFor(b) };
};

const roundTo = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

/**
 * Percentage-point difference between two rates, or null when either side
 * has no rate (no checks must never be compared as 0%).
 */
export const rateDeltaPp = (current, previous) =>
  isFiniteNumber(current) && isFiniteNumber(previous)
    ? roundTo(Number(current) - Number(previous))
    : null;

/**
 * Absolute and relative change between two counts. `pct` is null when the
 * baseline is zero; `isNew` marks growth from a zero baseline.
 */
export const countDelta = (current, previous) => {
  if (!isFiniteNumber(current) || !isFiniteNumber(previous)) return null;
  const currentValue = Number(current);
  const previousValue = Number(previous);
  const abs = currentValue - previousValue;
  return {
    abs,
    pct: previousValue === 0 ? null : roundTo((abs / previousValue) * 100, 1),
    isNew: previousValue === 0 && currentValue > 0,
  };
};

/**
 * Classify a delta for display: "better", "worse", "neutral" or "na".
 */
export const classifyDelta = (
  delta,
  { positiveIsGood = true, neutralBand = 0 } = {},
) => {
  if (!isFiniteNumber(delta)) return "na";
  const value = Number(delta);
  if (value === 0 || Math.abs(value) < neutralBand) return "neutral";
  return (positiveIsGood ? value > 0 : value < 0) ? "better" : "worse";
};

export const isLowSample = (...checkCounts) =>
  checkCounts.some(
    (count) => !isFiniteNumber(count) || Number(count) < LOW_SAMPLE_CHECKS,
  );

/**
 * Map rows by key (first row wins) for joining one period's rows to the other.
 */
export const indexByKey = (rows, keyFn) => {
  const index = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = keyFn(row);
    if (!index.has(key)) index.set(key, row);
  });
  return index;
};

export const dayOfMonth = (isoDate) => {
  const day = Number(String(isoDate || "").slice(8, 10));
  return Number.isInteger(day) && day >= 1 && day <= 31 ? day : null;
};

/**
 * Align two daily trends by day of month. Returns `days` (1..N) plus
 * `current` and `previous` arrays holding the bucket for each day, or null
 * where that period has no bucket for the day.
 */
export const alignTrendByDayOfMonth = (currentTrend, previousTrend) => {
  const byDay = (trend) => {
    const map = new Map();
    (Array.isArray(trend) ? trend : []).forEach((bucket) => {
      const day = dayOfMonth(bucket?.date || bucket?.periodStart);
      if (day && !map.has(day)) map.set(day, bucket);
    });
    return map;
  };
  const current = byDay(currentTrend);
  const previous = byDay(previousTrend);
  const lastDay = Math.max(0, ...current.keys(), ...previous.keys());
  const days = Array.from({ length: lastDay }, (_, index) => index + 1);
  return {
    days,
    current: days.map((day) => current.get(day) || null),
    previous: days.map((day) => previous.get(day) || null),
  };
};

/**
 * Sum per-item check counts across all trend buckets. Each submission entry
 * is counted in exactly one bucket, so this reproduces the period's per-item
 * totals even for items a ranked list (e.g. repeat out-of-stock) omitted.
 */
export const sumTrendItems = (trend, keyFn) => {
  const totals = new Map();
  (Array.isArray(trend) ? trend : []).forEach((bucket) => {
    (Array.isArray(bucket?.byItem) ? bucket.byItem : []).forEach((item) => {
      const key = keyFn(item);
      const total = totals.get(key) || { totalChecks: 0, trueOOSCount: 0 };
      total.totalChecks += Number(item?.totalChecks) || 0;
      total.trueOOSCount += Number(item?.trueOOSCount) || 0;
      totals.set(key, total);
    });
  });
  totals.forEach((total) => {
    total.trueOOSRate =
      total.totalChecks > 0
        ? roundTo((total.trueOOSCount / total.totalChecks) * 100)
        : null;
  });
  return totals;
};

/**
 * Per-client visit counts for a period. Prefers the complete `byClient`
 * pairs; falls back to the ranked lists plus unvisited clients (0) for
 * responses from an older API, returning null where the count is unknown.
 */
export const createVisitLookup = (dashboard) => {
  const counts = new Map();
  const byClient = dashboard?.visits?.byClient;
  if (Array.isArray(byClient)) {
    byClient.forEach(([clientId, visits]) =>
      counts.set(Number(clientId), Number(visits) || 0),
    );
    return (clientId) => counts.get(Number(clientId)) ?? 0;
  }

  [
    ...(dashboard?.visits?.mostVisited || []),
    ...(dashboard?.visits?.leastVisited || []),
  ].forEach((row) => {
    const visits = Number(row?.visits ?? row?.visitCount);
    if (row?.clientId != null && Number.isFinite(visits)) {
      counts.set(Number(row.clientId), visits);
    }
  });
  (dashboard?.unvisitedClients?.clients || []).forEach((row) => {
    if (row?.clientId != null) counts.set(Number(row.clientId), 0);
  });
  return (clientId) =>
    counts.has(Number(clientId)) ? counts.get(Number(clientId)) : null;
};

/**
 * Merge two option lists by `value`, keeping the first label, sorted by the
 * same comparator the dashboard uses.
 */
export const unionOptions = (left, right, compare) => {
  const merged = new Map();
  [...(left || []), ...(right || [])].forEach((option) => {
    if (option && !merged.has(option.value)) merged.set(option.value, option);
  });
  const values = [...merged.values()];
  return compare ? values.sort(compare) : values;
};

// Rows need at least this many stock checks in both months to be ranked as
// a "biggest change", so one or two visits cannot top the list.
export const MOVER_MIN_CHECKS = 10;

/**
 * Rank rows present in both months by the change of a rate. Returns the
 * largest improvements and declines (beyond the neutral band) and the stable
 * rows (change within the band), each entry
 * `{ key, row, previous, before, after, delta }`. `unmatched` lists rows that
 * cannot be compared (missing, or below `minChecks`, in either month).
 */
export const rankMovers = (
  currentRows,
  previousRows,
  {
    keyFn,
    rateFn,
    checksFn,
    positiveIsGood = false,
    minChecks = MOVER_MIN_CHECKS,
    limit = 5,
  },
) => {
  const previousIndex = indexByKey(previousRows, keyFn);
  const currentKeys = new Set();
  const changes = [];
  const unmatched = [];
  (Array.isArray(currentRows) ? currentRows : []).forEach((row) => {
    const key = keyFn(row);
    currentKeys.add(key);
    const previous = previousIndex.get(key) || null;
    const enoughChecks = (candidate) =>
      !checksFn || Number(checksFn(candidate)) >= minChecks;
    const before = previous ? rateFn(previous) : null;
    const after = rateFn(row);
    const delta =
      previous && enoughChecks(row) && enoughChecks(previous)
        ? rateDeltaPp(after, before)
        : null;
    if (delta === null) {
      unmatched.push({ key, row, previous, before, after, delta: null });
      return;
    }
    changes.push({ key, row, previous, before, after, delta });
  });
  previousIndex.forEach((previous, key) => {
    if (!currentKeys.has(key)) {
      unmatched.push({
        key,
        row: null,
        previous,
        before: rateFn(previous),
        after: null,
        delta: null,
      });
    }
  });
  const isStable = (change) => Math.abs(change.delta) < NEUTRAL_BAND_PP;
  const moved = changes.filter((change) => !isStable(change));
  const isImprovement = (change) =>
    positiveIsGood ? change.delta > 0 : change.delta < 0;
  const byMagnitude = (a, b) => Math.abs(b.delta) - Math.abs(a.delta);
  return {
    improved: moved.filter(isImprovement).sort(byMagnitude).slice(0, limit),
    declined: moved
      .filter((change) => !isImprovement(change))
      .sort(byMagnitude)
      .slice(0, limit),
    // Most-checked stable rows first: the steadiest, best-evidenced ones.
    stable: changes
      .filter(isStable)
      .sort(
        (a, b) =>
          (Number(checksFn?.(b.row)) || 0) - (Number(checksFn?.(a.row)) || 0),
      )
      .slice(0, limit),
    unmatched,
  };
};
